import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { IPairWallet } from 'api/apiPairs/models';
import { bn, bnFrom } from 'tools/math';
import { IDexPair } from 'types/pairs';
import { Button } from 'ui/Button/Button';
import { Modal } from 'ui/Modal/Modal';

import { StepAllocate } from './StepAllocate';
import { StepSelectRecipients, StepSelectRecipientsHandle } from './StepSelectRecipients';

import { useCreateMultiTransfer } from '../../api/useCreateMultiTransfer';
import { resetTransferWizardState, useTransferWizardStore } from '../../model/transferWizard.store';

interface ModalTransferWizardProps {
  pair: IDexPair;
  sourceWallet: IPairWallet;
  onRemountTable: () => void;
  onClose: () => void;
}

export const ModalTransferWizard = ({
  pair,
  sourceWallet,
  onRemountTable,
  onClose,
}: ModalTransferWizardProps) => {
  const step = useTransferWizardStore(state => state.step);
  const setStep = useTransferWizardStore(state => state.setStep);
  const setSourceWallet = useTransferWizardStore(state => state.setSourceWallet);
  const token = useTransferWizardStore(state => state.token);
  const recipients = useTransferWizardStore(state => state.recipients);
  const isSubmitting = useTransferWizardStore(state => state.isSubmitting);
  const setIsSubmitting = useTransferWizardStore(state => state.setIsSubmitting);

  const [canProceed, setCanProceed] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const createTransferMutation = useCreateMultiTransfer(pair.id);
  const stepSelectRef = useRef<StepSelectRecipientsHandle>(null);

  useEffect(() => {
    setSourceWallet(sourceWallet);
    setStep('selectRecipients');

    return () => {
      resetTransferWizardState();
    };
  }, [sourceWallet, setSourceWallet, setStep]);

  const handleClose = () => {
    resetTransferWizardState();
    onClose();
  };

  const handleSubmit = async () => {
    if (!token) return;
    setIsSubmitting(true);

    try {
      const payload = {
        wallet_src_id: sourceWallet.id,
        token_id: token.id,
        dst: recipients.map(recipient => ({
          wallet_id: recipient.walletId,
          amount: bn(recipient.amount, token.decimals).toString(),
        })),
      };

      const response = await createTransferMutation.mutateAsync(payload);

      if (!response.isSuccess || !response.data) {
        toast.error(response.errorMessage ?? 'Failed to create task');
        return;
      }

      toast.success(`Multi-transfer task created (job ${response.data.job_id})`);

      onRemountTable();
      handleClose();
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSourceTokenBalance = useMemo(() => {
    if (!token) return false;

    const balance =
      sourceWallet.tokens?.find(walletToken => walletToken.token.id === token.id)?.balance ?? '0';

    return !bnFrom(balance).isZero();
  }, [sourceWallet, token]);

  const handleNext = () => {
    if (!hasSourceTokenBalance) {
      stepSelectRef.current?.triggerBalanceWarning();
      return;
    }

    setStep('allocate');
  };

  const currentStepContent = useMemo(() => {
    if (step === 'selectRecipients') {
      return (
        <StepSelectRecipients
          ref={stepSelectRef}
          pair={pair}
          sourceWallet={sourceWallet}
          onReadyChange={setCanProceed}
        />
      );
    }

    return <StepAllocate pair={pair} sourceWallet={sourceWallet} onValidityChange={setCanSubmit} />;
  }, [step, pair, sourceWallet]);

  const stepTitle = step === 'selectRecipients' ? 'Select recipients' : 'Allocate amounts';

  return (
    <Modal
      title={`Transfer funds · ${stepTitle}`}
      onClose={handleClose}
      className="w-[720px]"
      customButtons={
        <div className="flex justify-between w-full">
          <div className="flex gap-2">
            <Button color="transparent" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            {step === 'allocate' && (
              <Button
                color="secondary"
                onClick={() => setStep('selectRecipients')}
                disabled={isSubmitting}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 'selectRecipients' && (
              <Button color="primary" disabled={!canProceed} onClick={handleNext}>
                Next
              </Button>
            )}
            {step === 'allocate' && (
              <Button color="primary" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
                {isSubmitting ? 'Creating…' : 'Send'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6">{currentStepContent}</div>
    </Modal>
  );
};
